import React, { useState, useEffect } from 'react';

import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import {
    Box,
    Button,
    Divider,
    Stack,
    Typography,
    IconButton,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import KeyIndicator from 'common/components/KeyIndicator';
import { useBreakpoint } from 'common/hooks';

const KeyIndicators = ({ contactDetails, handleClickUpdate }) => {
    const { t } = useTranslation();
    const breakpoint = useBreakpoint();
    const hideKeyIndicators = ['xs', 'sm', 'md'].includes(breakpoint);
    const [isExpanded, setIsExpanded] = useState(true);
    const contact = contactDetails?.contact || contactDetails;

    useEffect(() => {
        if (['lg', 'xl'].includes(breakpoint)) {
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
        }
    }, [breakpoint]);

    const {
        company_name,
        company_position,
        emails = [],
        name,
        rfc,
        telephones = [],
        user,
    } = contact || {};

    const setType = (phone_type) => {
        switch (phone_type) {
            case '0':
                return t('Mobile');
            case '1':
                return t('Office');
            case '2':
                return t('Home');
            case '3':
                return t('Other');
            default:
                return t('Telephone');
        }
    };
    const setMailType = (mail_type) => {
        switch (mail_type) {
            case '0':
                return t('Work');
            case '1':
                return t('Personal');
            case '2':
                return t('Other');
            default:
                return t('Email');
        }
    };

    return (
        <Box id="contact-information">
            <Box
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                sx={{ mt: { md: 2, xs: 0 } }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <PersonIcon sx={{ color: 'primary.main' }} />
                    <Typography fontWeight="bold" variant="body2">
                        {t('Contact information')}
                    </Typography>
                </Box>
                <IconButton onClick={() => handleClickUpdate(contact)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Stack>
                <KeyIndicator
                    svgIcon={<PersonIcon />}
                    title={t('Name')}
                    value={String(name || t('Not specified'))}
                />

                {(isExpanded || !hideKeyIndicators) && (
                    <>
                        {company_name && (
                            <KeyIndicator
                                svgIcon={<BusinessIcon />}
                                title={t('Company')}
                                value={String(company_name)}
                            />
                        )}

                        {company_position && (
                            <KeyIndicator
                                svgIcon={<BusinessIcon />}
                                title={t('Position')}
                                value={String(company_position)}
                            />
                        )}

                        {rfc && (
                            <KeyIndicator
                                svgIcon={<LocationOnIcon />}
                                title={'RFC'}
                                value={String(rfc)}
                            />
                        )}

                        {emails.length > 0 && (
                            <>
                                {emails.map((email, index) => (
                                    <KeyIndicator
                                        key={`email-${index}`}
                                        svgIcon={<EmailIcon />}
                                        title={setMailType(email?.email_type)}
                                        value={
                                            typeof email === 'string'
                                                ? email
                                                : email?.email ||
                                                  email?.value ||
                                                  String(email)
                                        }
                                    />
                                ))}
                            </>
                        )}

                        {telephones.length > 0 && (
                            <>
                                {telephones.map((phone, index) => {
                                    const phoneNumber =
                                        typeof phone === 'string'
                                            ? phone
                                            : phone?.telephone ||
                                              phone?.phone ||
                                              phone?.value ||
                                              String(phone);

                                    const extension = phone?.extension;
                                    const displayValue = extension
                                        ? `${phoneNumber}-${extension}`
                                        : phoneNumber;

                                    return (
                                        <KeyIndicator
                                            key={`phone-${index}`}
                                            svgIcon={<PhoneIcon />}
                                            title={setType(phone?.phone_type)}
                                            value={displayValue}
                                        />
                                    );
                                })}
                            </>
                        )}

                        {user && (
                            <KeyIndicator
                                svgIcon={<PersonIcon />}
                                title={t('Installer')}
                                value={String(
                                    user.first_name + ' ' + user.last_name,
                                )}
                            />
                        )}
                    </>
                )}
            </Stack>

            {hideKeyIndicators && (
                <Box mt={2} textAlign="center">
                    <Button
                        onClick={() => setIsExpanded((prev) => !prev)}
                        endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                        variant="text"
                    >
                        {isExpanded ? t('Less') : t('More')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

KeyIndicators.propTypes = {
    contactDetails: PropTypes.object,
    handleClickUpdate: PropTypes.func.isRequired,
};

export default KeyIndicators;
